import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    OutlinedInput,
    Checkbox
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { convertDate, validateEmail, validateNumber } from 'utils/commonHelpers';

const EditReportDeliveryDialog = ({ onClose, onCloseCancel, data, reportsData, usersData }) => {
    const [deliveryDate, setDeliveryDate] = useState(data.delivery_date);
    const [frequencyType, setFrequencyType] = useState(data.freqtype);
    const [frequencyValue, setFrequencyValue] = useState(data.frequency);
    const [reportsId, setReportsId] = useState(data.report_id);
    const [loading, setLoading] = useState(false);
    const [nonRegisteredEmail, setNonRegisteredEmail] = useState('');
    const [usersId, setUsersId] = useState([]);

    useEffect(() => {
        const emails = data.emails.split(',').map((item) => item.trim());
        let originalNonRegisteredEmail = [];
        let originalUsersId = [];
        const userEmails = usersData.map((item) => item.email);
        for (let i = 0; i < emails.length; i++) {
            if (!userEmails.includes(emails[i])) {
                originalNonRegisteredEmail.push(emails[i]);
            } else {
                originalUsersId.push(emails[i]);
            }
        }
        setUsersId(originalUsersId);
        setNonRegisteredEmail(originalNonRegisteredEmail.join(', '));
    }, [data.emails, usersData]);

    const handleChangeUsersId = (event) => {
        const {
            target: { value }
        } = event;
        setUsersId(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSubmit = () => {
        if (convertDate(deliveryDate) === 'Invalid date') {
            toast.error('The delivery date format is incorrect');
            return true;
        }
        if (validateNumber(frequencyValue, 'integer') !== true) {
            toast.error('The frequency value should be an integer');
            return true;
        }
        if (reportsId === 0) {
            toast.error('Please select the report');
            return true;
        }
        if (usersId.length === 0 && nonRegisteredEmail === '') {
            toast.error('Please select the user(s) or type the email address(es) by comma');
            return true;
        }
        let deliveryEmails;
        if (nonRegisteredEmail !== '') {
            const emails = nonRegisteredEmail.split(',').map((item) => item.trim());
            for (let i = 0; i < emails.length; i++) {
                if (!validateEmail(emails[i])) {
                    toast.error('Please correct the email format');
                    return true;
                }
            }
            deliveryEmails = usersId.concat(emails);
        } else {
            deliveryEmails = usersId;
        }

        ApiHelper.put(`/api/automation/delivery_reports/${data.id}/`, {
            frequency_type: frequencyType,
            frequency_value: frequencyValue,
            reports_id: reportsId,
            delivery_date: convertDate(deliveryDate),
            emails: deliveryEmails
        })
            .then(() => {
                toast.success('Successfully updated');
                setLoading(false);
                onClose();
            })
            .catch(() => {
                toast.error('Failed to update the delivery report');
                setLoading(false);
            });
    };

    return (
        <Dialog open={true} onClose={onCloseCancel}>
            <DialogTitle sx={{ fontSize: '16px' }}>Edit Report Delivery</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mt: 2 }}>
                    <FormControl sx={{ width: '100%', mt: 1 }}>
                        <InputLabel>Frequency Type</InputLabel>
                        <Select label="Frequency Type" value={frequencyType} onChange={(e) => setFrequencyType(e.target.value)}>
                            <MenuItem value="DAILY">DAILY</MenuItem>
                            <MenuItem value="MONTHLY">MONTHLY</MenuItem>
                            <MenuItem value="ANNUALLY">ANNUALLY</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="Frequency Value"
                        type="number"
                        value={frequencyValue}
                        onChange={(e) => setFrequencyValue(e.target.value)}
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Delivery Date"
                            value={deliveryDate}
                            onChange={(newValue) => {
                                setDeliveryDate(newValue);
                            }}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <FormControl sx={{ width: '100%', mt: 1 }}>
                        <InputLabel>Reports</InputLabel>
                        <Select label="Reports" value={reportsId} onChange={(e) => setReportsId(e.target.value)}>
                            <MenuItem value={0}>None</MenuItem>
                            {reportsData.map((data, key) => (
                                <MenuItem key={key} value={data.id}>
                                    {data.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <FormControl sx={{ width: '100%', mt: 1 }}>
                        <InputLabel>Select the user</InputLabel>
                        <Select
                            displayEmpty
                            value={usersId}
                            name="usersId"
                            multiple
                            input={<OutlinedInput label="Select the user" />}
                            onChange={handleChangeUsersId}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {usersData.map((data, key) => (
                                <MenuItem value={data.email} key={key}>
                                    <Checkbox checked={usersId.indexOf(data.email) > -1} />
                                    {data.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="Email address (Non-registered)"
                        value={nonRegisteredEmail}
                        onChange={(e) => setNonRegisteredEmail(e.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseCancel}>Cancel</Button>
                <LoadingButton onClick={handleSubmit} loading={loading} color="primary" variant="contained">
                    <span>Submit</span>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

EditReportDeliveryDialog.propTypes = {
    data: PropTypes.object,
    onClose: PropTypes.func,
    onCloseCancel: PropTypes.func,
    reportsData: PropTypes.array,
    usersData: PropTypes.array
};

export default EditReportDeliveryDialog;
